import type {
  RiskScoreBreakdownAggregated,
  RiskScoreBreakdownChildContribution,
} from '@risksmart-app/domain/src/types/risk-score-breakdown';

import type { NarrativeTranslator, RiskScoreNarrative } from './buildNarrative';
import type {
  RiskScoreBreakdownControlTypeRow,
  RiskScoreBreakdownDetails,
  RiskScoreBreakdownDimensionRow,
} from './RiskScoreBreakdownPopoverView';

type ScoreTriple = NonNullable<RiskScoreBreakdownAggregated['inherent']>;
type Block = 'inherent' | 'residual';

const BLOCKS: readonly Block[] = ['inherent', 'residual'];

export interface AggregatedExplanationOptions {
  /** Translator scoped to the `riskScoreBreakdown.aggregated` keyPrefix. */
  t: NarrativeTranslator;
  /** Translator scoped to the `riskScoreBreakdown` keyPrefix. */
  tRoot: NarrativeTranslator;
  /** Translator scoped to the `riskScoreBreakdown.formula` keyPrefix. */
  tFormula: NarrativeTranslator;
  /**
   * Child `riskId` → display label. Titles are not stored in the payload, so
   * the caller resolves them at render time and must return a stable
   * fallback (the id) for a child it cannot see.
   */
  resolveChildLabel: (riskId: string) => string;
  formatValue: (value: number) => string;
}

const blockLabel = (block: Block, t: NarrativeTranslator): string =>
  t(block === 'inherent' ? 'inherentBlock' : 'residualBlock');

/** No block resolved: there is no score, so nothing describes how it formed. */
const hasParentScore = (breakdown: RiskScoreBreakdownAggregated): boolean =>
  breakdown.inherent !== null || breakdown.residual !== null;

const buildIntro = (
  breakdown: RiskScoreBreakdownAggregated,
  { t, resolveChildLabel }: AggregatedExplanationOptions
): string | undefined => {
  if (!hasParentScore(breakdown)) {
    return undefined;
  }

  if (breakdown.method === 'worst_case') {
    // The popover explains the residual score, so its source comes first;
    // the inherent source stands in when residual did not resolve.
    const residualId = breakdown.worstCase?.residualRiskId ?? null;
    const inherentId = breakdown.worstCase?.inherentRiskId ?? null;
    const sourceId = residualId ?? inherentId;
    if (sourceId === null) {
      return undefined;
    }

    const intro = t('introWorstCase', { child: resolveChildLabel(sourceId) });
    // Each block can be lifted from a different child, so saying only the
    // residual source would misattribute the inherent one.
    const namesBothSources =
      residualId !== null && inherentId !== null && inherentId !== residualId;

    return namesBothSources
      ? `${intro} ${t('introWorstCaseInherent', { child: resolveChildLabel(inherentId) })}`
      : intro;
  }

  return t('introAverage', {
    children: t('childCount', { count: breakdown.children.total }),
  });
};

const buildBlockSentences = (
  breakdown: RiskScoreBreakdownAggregated,
  block: Block,
  t: NarrativeTranslator
): string[] => {
  const label = blockLabel(block, t);
  const { excluded } = breakdown.children[block];
  const sentences: string[] = [];

  if (excluded > 0) {
    // "left out of the average" is wrong for worst case, which considers
    // rather than averages, so each method has its own phrasing.
    sentences.push(
      t(
        breakdown.method === 'average'
          ? 'excludedCount'
          : 'excludedCountWorstCase',
        { excluded, total: breakdown.children.total, block: label }
      )
    );
  }
  if (breakdown[block] === null) {
    sentences.push(
      t(
        breakdown.method === 'average'
          ? 'unresolvedBlock'
          : 'unresolvedBlockWorstCase',
        { block: label }
      )
    );
  }

  return sentences;
};

const buildRoundingSentence = (
  breakdown: RiskScoreBreakdownAggregated,
  t: NarrativeTranslator
): string | undefined => {
  // Worst case lifts a child's stored values verbatim; no rounding applies.
  // Neither does it when no block resolved — there is no average to describe.
  if (breakdown.method !== 'average' || !hasParentScore(breakdown)) {
    return undefined;
  }

  switch (breakdown.roundingMode) {
    case 'none':
      return t('roundingNone');
    case 'up':
    case 'down':
      // The adverb is a translated key; interpolating the raw enum value
      // would leave "up"/"down" in English in every locale.
      return t('roundingApplied', {
        mode: t(`roundingMode_${breakdown.roundingMode}`),
      });
    default:
      return undefined;
  }
};

/**
 * The parent's narrative: how the score was formed, which blocks fell short
 * and why, and the rounding applied when it is not the default.
 */
export const buildAggregatedNarrative = (
  breakdown: RiskScoreBreakdownAggregated,
  options: AggregatedExplanationOptions
): RiskScoreNarrative => ({
  paragraphs: [
    buildIntro(breakdown, options),
    ...BLOCKS.flatMap((block) =>
      buildBlockSentences(breakdown, block, options.t)
    ),
    buildRoundingSentence(breakdown, options.t),
  ].filter((paragraph): paragraph is string => paragraph !== undefined),
});

const formatEquation = (
  { likelihood, impact, score }: ScoreTriple,
  { tFormula, formatValue }: AggregatedExplanationOptions
): string =>
  tFormula('scoreLine', {
    likelihood: formatValue(likelihood),
    impact: formatValue(impact),
    score: formatValue(score),
  });

const buildChildRow = (
  { riskId, inherent, residual }: RiskScoreBreakdownChildContribution,
  options: AggregatedExplanationOptions
): RiskScoreBreakdownControlTypeRow => {
  const unscored = options.t('excludedMarker');

  return {
    id: riskId,
    label: options.resolveChildLabel(riskId),
    count: inherent ? formatEquation(inherent, options) : unscored,
    value: residual ? formatEquation(residual, options) : unscored,
  };
};

const buildParentRow = (
  breakdown: RiskScoreBreakdownAggregated,
  block: Block,
  { t, tRoot, formatValue }: AggregatedExplanationOptions
): RiskScoreBreakdownDimensionRow => {
  const triple = breakdown[block];

  return {
    label: blockLabel(block, t),
    aggregate: triple
      ? `${formatValue(triple.likelihood)} × ${formatValue(triple.impact)}`
      : tRoot('emDash'),
    reduction: triple ? formatValue(triple.score) : t('excludedMarker'),
  };
};

/**
 * The parent's details on the popover's existing grid: one row per child with
 * its inherent and residual values (or an unscored marker), then the parent's
 * own dimensions and score per block.
 */
export const buildAggregatedDetails = (
  breakdown: RiskScoreBreakdownAggregated,
  options: AggregatedExplanationOptions
): RiskScoreBreakdownDetails => ({
  controlTypesHeading: options.t('childrenHeading'),
  controlTypesHeaderRow: {
    label: '',
    count: blockLabel('inherent', options.t),
    value: blockLabel('residual', options.t),
  },
  controlTypes: breakdown.contributions.map((contribution) =>
    buildChildRow(contribution, options)
  ),
  dimensionsHeading: options.t(
    breakdown.method === 'average'
      ? 'parentValuesFooter'
      : 'parentValuesFooterWorstCase'
  ),
  dimensions: BLOCKS.map((block) => buildParentRow(breakdown, block, options)),
});
